import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Trophy, Medal, Award, TrendingUp, Calendar, Zap } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { format } from 'date-fns'

interface XPLedgerEntry {
  id: string
  transaction_type: string
  finish_placement?: number
  total_participants?: number
  placement_percentage?: number
  xp_awarded: number
  vp_awarded: number
  multiplier: number
  realm_level_pre: string
  aura_xp_pre: number
  realm_level_post: string
  aura_xp_post: number
  description?: string
  created_at: string
}

export function XPLedger() {
  const { user } = useAuth()
  const [entries, setEntries] = useState<XPLedgerEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchLedger = async () => {
      if (!user) return

      try {
        const { data, error } = await supabase
          .from('xp_ledger')
          .select('*')
          .eq('player_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50)

        if (error) throw error
        setEntries(data || [])
      } catch (error) {
        console.error('Error fetching XP ledger:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchLedger()
  }, [user])

  const getTransactionIcon = (type: string, placement?: number) => {
    switch (type) {
      case 'placement_award':
        if (placement === 1) return <Trophy className="h-4 w-4 text-yellow-500" />
        if (placement === 2) return <Medal className="h-4 w-4 text-gray-400" />
        if (placement === 3) return <Award className="h-4 w-4 text-amber-600" />
        return <TrendingUp className="h-4 w-4 text-primary" />
      case 'consistency_bonus':
        return <Zap className="h-4 w-4 text-success" />
      case 'manual_adjustment':
        return <Calendar className="h-4 w-4 text-muted-foreground" />
      default:
        return <TrendingUp className="h-4 w-4" />
    }
  }

  const getTransactionLabel = (entry: XPLedgerEntry) => {
    switch (entry.transaction_type) {
      case 'placement_award':
        if (entry.finish_placement && entry.total_participants) {
          const pct = ((entry.finish_placement / entry.total_participants) * 100).toFixed(1)
          return `#${entry.finish_placement} of ${entry.total_participants} (${pct}%)`
        }
        return 'League Finish'
      case 'consistency_bonus':
        return 'Consistency Bonus'
      case 'manual_adjustment':
        return 'Manual Adjustment'
      default:
        return 'Unknown'
    }
  }

  const getBadgeVariant = (type: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (type) {
      case 'placement_award': return 'default'
      case 'consistency_bonus': return 'secondary'
      case 'manual_adjustment': return 'outline'
      default: return 'outline'
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            AURA XP Ledger
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Loading transaction history...
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          AURA XP Transaction Ledger
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length > 0 ? (
          <ScrollArea className="h-96">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>XP Change</TableHead>
                  <TableHead>Realm Change</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(entry.transaction_type, entry.finish_placement)}
                        <div>
                          <Badge variant={getBadgeVariant(entry.transaction_type)} className="text-xs">
                            {getTransactionLabel(entry)}
                          </Badge>
                          {entry.multiplier > 1 && (
                            <Badge variant="outline" className="text-xs ml-1">
                              {entry.multiplier}x
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {entry.description || 'No description'}
                    </TableCell>
                    <TableCell>
                      <div className="font-mono">
                        <div className="text-success font-medium">
                          +{entry.xp_awarded.toLocaleString()} AXP
                        </div>
                        {entry.vp_awarded > 0 && (
                          <div className="text-xs text-primary">
                            +{entry.vp_awarded.toLocaleString()} VP
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {entry.realm_level_pre === entry.realm_level_post ? (
                          <span className="text-muted-foreground">{entry.realm_level_post}</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">{entry.realm_level_pre}</span>
                            <span className="text-muted-foreground">→</span>
                            <Badge variant="default" className="text-xs">
                              {entry.realm_level_post}
                            </Badge>
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground font-mono">
                          {entry.aura_xp_pre.toLocaleString()} → {entry.aura_xp_post.toLocaleString()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(entry.created_at), 'MMM d, HH:mm')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No AURA XP transactions yet</p>
            <p className="text-sm">Complete leagues to start earning XP!</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}